import { useEffect, useMemo } from "react";
import { useAtom } from "jotai";
import useStore from "../../../context/useStore";
import dataService from "../../../helpers/dataService";
import {
  getActiveFilterChips,
  groupFilterDefinitions,
  splitFilterDefinitions,
  useListSearch,
} from "../../common/DataList";
import {
  ORIENTATION_PARTNERS_ENDPOINT,
  RECIPIENT_DEFAULT_FILTERS,
  RECIPIENT_FILTER_GROUPS,
  RECIPIENT_SEARCH_ENDPOINT,
  buildRecipientSearchPayload,
  getRecipientFilterDefinitions,
  normalizeConstants,
  orientationPartnersAtom,
  recipientResultsAtom,
  recipientSearchAtom,
} from "./recipientListConfig";
import { getRecipientColumns } from "./recipientColumns";

// Assemble l'état de liste générique (useListSearch) et le métier allocataire :
// constantes du store, partenaires d'orientation, définitions de filtres et
// colonnes. L'export de la liste vit dans le module d'exports (type
// LISTE_ALLOCATAIRE_FLUX), plus dans un bouton de cette page.
export default function useRecipientList() {
  const [state] = useStore();
  const constants = normalizeConstants(state.constants.items);
  const [orgs, setOrgs] = useAtom(orientationPartnersAtom);

  const list = useListSearch({
    searchAtom: recipientSearchAtom,
    resultsAtom: recipientResultsAtom,
    defaultFilters: RECIPIENT_DEFAULT_FILTERS,
    endpoint: RECIPIENT_SEARCH_ENDPOINT,
    buildPayload: buildRecipientSearchPayload,
    // Pas de recherche au montage tant que POST recipients/search n'est pas
    // optimisé côté API (~16 s en prod) — repasser à true une fois l'API optimisée.
    autoSearch: false,
  });

  useEffect(() => {
    if (orgs.length === 0) dataService.get(ORIENTATION_PARTNERS_ENDPOINT, setOrgs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const definitions = useMemo(
    () => getRecipientFilterDefinitions({ constants, orgs }),
    [constants, orgs]
  );
  const { principal, secondary } = useMemo(() => splitFilterDefinitions(definitions), [definitions]);
  const groups = useMemo(
    () => groupFilterDefinitions(definitions, RECIPIENT_FILTER_GROUPS),
    [definitions]
  );
  const columns = useMemo(() => getRecipientColumns({ constants, orgs }), [constants, orgs]);

  // Les chips portent tous les filtres (la recherche libre a son champ) ; le
  // bouton du panneau ne compte que ceux restés dans le panneau.
  const chips = getActiveFilterChips(list.filters, definitions, RECIPIENT_DEFAULT_FILTERS);
  const panelChips = getActiveFilterChips(list.filters, secondary, RECIPIENT_DEFAULT_FILTERS);
  const canReset = chips.length > 0 || !!list.filters.terms;

  return {
    ...list,
    activeFiltersCount: chips.length,
    panelActiveFiltersCount: panelChips.length,
    canReset,
    constants,
    orgs,
    definitions,
    principalFilters: principal,
    groups,
    columns,
    chips,
  };
}
